import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationLocalAssaysProtocolsModal
    from '@/modules/configurationModule/detail/local-assays/protocols/components/modal/configuration-local-assays-protocols-modal.vue'
import CodificationSearcherModal from '@/modules/configurationModule/detail/samples/CodificationSearcherModal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import {
    ConfigurationLocalAssaysProtocolsGridState,
    initialConfigurationLocalAssaysProtocolsGridState
} from '@/modules/configurationModule/detail/local-assays/protocols/components/grid/configuration-local-assays-protocols-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import {
    ConfigurationProtocolCreateRequest,
    ConfigurationProtocolPatchInBatchRequest,
    ConfigurationProtocolResponse
} from '@/modules/configurationModule/detail/local-assays/protocols/services/configuration-protocol/configuration-protocol.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationLocalAssaysProtocolsModal,
        CodificationSearcherModal
    }
})
export default class ConfigurationLocalAssaysProtocolsGrid extends BaseCrudServerSideComponent<ConfigurationProtocolResponse> implements BaseCrud {
    @Ref() readonly configurationLocalAssaysProtocolsModalWrapper!: typeof ModalWrapper

    state: ConfigurationLocalAssaysProtocolsGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationLocalAssaysProtocolsGridState()
    }

    /// SERVICES CALLS ///

    private async _createProtocol(createRequest: ConfigurationProtocolCreateRequest): Promise<ConfigurationProtocolResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchProtocols(batchRequests: ConfigurationProtocolPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteProtocols(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, protocols: ConfigurationProtocolResponse[]): Promise<void> {
        if (protocols?.length) {
            const protocolsToPatch =
                protocols.filter(protocol => protocol.active !== active)
                    .map(protocol => ({
                        id: protocol.id,
                        active
                    }))
            if (await this._patchProtocols(protocolsToPatch)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: ConfigurationProtocolCreateRequest): Promise<void> {
        if (createRequest) {
            const protocol = await this._createProtocol(createRequest)
            if (protocol) {
                this.reloadData()
                this.onUpdate(protocol)
            }
        }
    }

    async patchOperation(batchRequest: ConfigurationProtocolPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchProtocols([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteProtocols(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(protocol: ConfigurationProtocolResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (protocol) {
            this.showModal(this.$tc('configuration.actions.update'), protocol, this.configurationLocalAssaysProtocolsModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const assaysToDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map((assaySection: ConfigurationProtocolResponse) => assaySection.id)
            }
            await this.deleteOperation(assaysToDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.configurationLocalAssaysProtocolsModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }
}
